import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Mail, Lock, User, Loader2, Eye, EyeOff, ArrowLeft, ShieldCheck } from 'lucide-react';

export default function AuthModal({ open, onOpenChange, onSuccess }) {
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'forgot' | 'verify_code' | 'verify_email'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const { login, register, loginWithGoogle, forgotPassword, verifyCode, verifyEmail, resendCode } = useAuth();
  const navigate = useNavigate();

  const resetState = () => { setError(''); setCode(''); setResendCooldown(0); };

  // Wipe ALL form fields whenever the modal closes — re-opening "Sign In" must
  // always show an empty Welcome-back form so a random person on a shared device
  // can't see (or auto-submit) credentials a previous user typed in.
  useEffect(() => {
    if (!open) {
      setMode('login');
      setEmail('');
      setPassword('');
      setName('');
      setCode('');
      setError('');
      setShowPassword(false);
      setResendCooldown(0);
    }
  }, [open]);

  const startResendCooldown = () => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      if (mode === 'verify_code') {
        await forgotPassword(email);
      } else {
        await resendCode(email);
      }
      startResendCooldown();
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      let userData;
      if (mode === 'login') {
        userData = await login(email, password);
        if (userData.status === 'verify_email') {
          setMode('verify_email');
          startResendCooldown();
          setLoading(false);
          return;
        }
        onOpenChange(false);
        if (onSuccess) onSuccess(userData);
      } else if (mode === 'register') {
        userData = await register(email, password, name);
        if (userData.status === 'verify_email') {
          setMode('verify_email');
          startResendCooldown();
          setLoading(false);
          return;
        }
        onOpenChange(false);
        if (!userData.profile_completed) {
          navigate('/complete-profile');
        } else if (onSuccess) {
          onSuccess(userData);
        }
      } else if (mode === 'verify_email') {
        userData = await verifyEmail(email, code);
        onOpenChange(false);
        if (!userData.profile_completed) {
          navigate('/complete-profile');
        } else if (onSuccess) {
          onSuccess(userData);
        }
      } else if (mode === 'forgot') {
        await forgotPassword(email);
        setMode('verify_code');
        startResendCooldown();
      } else if (mode === 'verify_code') {
        userData = await verifyCode(email, code);
        onOpenChange(false);
        if (!userData.profile_completed) {
          navigate('/complete-profile');
        } else if (onSuccess) {
          onSuccess(userData);
        }
      }
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.response?.status) {
        setError(`Request failed (${err.response.status}). Please try again.`);
      } else if (err.code === 'ERR_NETWORK') {
        setError('Network error — please check your connection and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-sm mx-4 p-6" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-[#2C2A29]">
            {mode === 'login' && 'Welcome back'}
            {mode === 'register' && 'Create account'}
            {mode === 'forgot' && 'Reset password'}
            {mode === 'verify_code' && 'Enter code'}
            {mode === 'verify_email' && 'Verify your email'}
          </DialogTitle>
          <DialogDescription className="font-body text-sm text-[#78716C]">
            {mode === 'login' && 'Sign in to your account'}
            {mode === 'register' && 'Join the beauty community'}
            {mode === 'forgot' && 'We\'ll send a code to your email'}
            {mode === 'verify_code' && `We sent a 6-digit code to ${email}`}
            {mode === 'verify_email' && `We sent a 6-digit code to ${email}`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3 mt-2" data-testid="auth-form">
          {/* Verify Email Mode */}
          {mode === 'verify_email' && (
            <>
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100 flex items-start gap-3" data-testid="verify-email-notice">
                <Mail size={20} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-body text-sm text-emerald-800 font-semibold">Check your inbox</p>
                  <p className="font-body text-xs text-emerald-700 mt-0.5">Enter the 6-digit code we sent to <strong>{email}</strong></p>
                </div>
              </div>
              <div className="relative">
                <ShieldCheck size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A89F91]" />
                <input type="text" inputMode="numeric" maxLength={6} required value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full bg-cream-50 border border-cream-100 rounded-xl pl-10 pr-4 py-3 font-body text-lg text-center tracking-[0.5em] text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                  placeholder="000000" data-testid="verify-email-code-input" />
              </div>
              <div className="text-center">
                <button type="button" onClick={handleResend} disabled={resendCooldown > 0}
                  className="text-xs font-body text-[#C5A880] hover:text-[#2C2A29] transition-colors disabled:text-[#A89F91] disabled:cursor-not-allowed"
                  data-testid="resend-code-btn">
                  {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
            </>
          )}

          {/* Name (register only) */}
          {mode === 'register' && (
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A89F91]" />
              <input type="text" required value={name} onChange={e => setName(e.target.value)}
                className="w-full bg-cream-50 border border-cream-100 rounded-xl pl-10 pr-4 py-3 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                placeholder="Full name" data-testid="auth-name-input" />
            </div>
          )}

          {/* Email (login, register, forgot) */}
          {(mode === 'login' || mode === 'register' || mode === 'forgot') && (
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A89F91]" />
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                className="w-full bg-cream-50 border border-cream-100 rounded-xl pl-10 pr-4 py-3 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                placeholder="Email address" data-testid="auth-email-input" />
            </div>
          )}

          {/* Password (login, register) */}
          {(mode === 'login' || mode === 'register') && (
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A89F91]" />
              <input type={showPassword ? 'text' : 'password'} required value={password} onChange={e => setPassword(e.target.value)}
                className="w-full bg-cream-50 border border-cream-100 rounded-xl pl-10 pr-10 py-3 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                placeholder="Password" data-testid="auth-password-input" />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A89F91] hover:text-[#2C2A29]"
                data-testid="toggle-password-btn">
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          )}

          {/* Code (verify_code — forgot password flow) */}
          {mode === 'verify_code' && (
            <>
              <div className="relative">
                <ShieldCheck size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A89F91]" />
                <input type="text" inputMode="numeric" maxLength={6} required value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full bg-cream-50 border border-cream-100 rounded-xl pl-10 pr-4 py-3 font-body text-lg text-center tracking-[0.5em] text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                  placeholder="000000" data-testid="auth-code-input" />
              </div>
              <div className="text-center">
                <button type="button" onClick={handleResend} disabled={resendCooldown > 0}
                  className="text-xs font-body text-[#C5A880] hover:text-[#2C2A29] transition-colors disabled:text-[#A89F91] disabled:cursor-not-allowed"
                  data-testid="resend-forgot-code-btn">
                  {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
            </>
          )}

          {error && (
            <div className="bg-red-50 text-red-600 rounded-xl px-4 py-2.5 text-xs font-body border border-red-100" data-testid="auth-error">
              {error}
            </div>
          )}

          <button type="submit" disabled={loading}
            className="w-full bg-[#F4C2C2] text-[#2C2A29] rounded-full py-3 font-body font-semibold transition-all hover:bg-[#ECAEAE] hover:shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="auth-submit-btn">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Processing...</> :
              mode === 'login' ? 'Sign In' :
              mode === 'register' ? 'Create Account' :
              mode === 'forgot' ? 'Send Code' :
              mode === 'verify_code' ? 'Verify & Login' :
              mode === 'verify_email' ? 'Verify Email' : 'Submit'}
          </button>

          {/* Google login */}
          {(mode === 'login' || mode === 'register') && (
            <>
              <div className="flex items-center gap-3 my-1">
                <div className="flex-1 border-t border-cream-100"></div>
                <span className="text-xs font-body text-[#A89F91]">or</span>
                <div className="flex-1 border-t border-cream-100"></div>
              </div>
              <button type="button"
                className="w-full border border-cream-100 text-[#2C2A29] rounded-full py-3 font-body font-semibold transition-all hover:bg-cream-50 flex items-center justify-center gap-2"
                data-testid="google-login-btn"
                onClick={() => { onOpenChange(false); setTimeout(() => { window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/')}`; }, 100); }}>
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-4 h-4" />
                Continue with Google
              </button>
            </>
          )}

          {/* Mode switchers */}
          <div className="text-center pt-1">
            {mode === 'login' && (
              <>
                <button type="button" onClick={() => { setMode('forgot'); resetState(); }}
                  className="text-xs font-body text-[#C5A880] hover:text-[#2C2A29] transition-colors"
                  data-testid="forgot-password-link">Forgot password?</button>
                <p className="text-xs font-body text-[#78716C] mt-2">
                  Don't have an account?{' '}
                  <button type="button" onClick={() => { setMode('register'); resetState(); }}
                    className="text-[#C5A880] font-semibold hover:text-[#2C2A29]" data-testid="switch-to-register">Sign up</button>
                </p>
              </>
            )}
            {mode === 'register' && (
              <p className="text-xs font-body text-[#78716C]">
                Already have an account?{' '}
                <button type="button" onClick={() => { setMode('login'); resetState(); }}
                  className="text-[#C5A880] font-semibold hover:text-[#2C2A29]" data-testid="switch-to-login">Sign in</button>
              </p>
            )}
            {(mode === 'forgot' || mode === 'verify_code' || mode === 'verify_email') && (
              <button type="button" onClick={() => { setMode('login'); resetState(); setCode(''); }}
                className="text-xs font-body text-[#C5A880] hover:text-[#2C2A29] transition-colors flex items-center justify-center gap-1 mx-auto"
                data-testid="back-to-login">
                <ArrowLeft size={12} /> Back to sign in
              </button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
